import React, { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SnoozeIcon from "@mui/icons-material/Snooze";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import { axiosInstance } from "../../../utils/networking";
import { ReminderType } from "../../../interfaces";
import ReminderEditModal from "./ReminderEditModal";

interface ReminderRowProps {
  reminder: ReminderType;
  onChanged: () => void;
}

const ReminderRow: React.FC<ReminderRowProps> = ({ reminder, onChanged }) => {
  const [snoozeAnchor, setSnoozeAnchor] = useState<null | HTMLElement>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const id = String(reminder._id);
  const overdue = moment(reminder.dueAt).isBefore(moment());

  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (err) {
      console.error("Reminder action failed:", err);
    } finally {
      setBusy(false);
    }
  };

  const complete = () =>
    run(() => axiosInstance.post(`/reminder/${id}/complete`, {}));
  const remove = () => run(() => axiosInstance.delete(`/reminder/${id}`));
  const snooze = (days: number) => {
    setSnoozeAnchor(null);
    run(() => axiosInstance.post(`/reminder/${id}/snooze`, { days }));
  };

  return (
    <Box
      sx={{
        p: 1,
        borderBottom: "1px solid #eee",
        backgroundColor: overdue ? "#fff4f4" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {reminder.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{ color: overdue ? "#c62828" : "#888" }}
            >
              {moment(reminder.dueAt).fromNow()}
            </Typography>
            {reminder.firm_name && (
              <Chip label={reminder.firm_name} size="small" variant="outlined" />
            )}
          </Stack>
        </Box>
        <Tooltip title="Complete">
          <span>
            <IconButton size="small" onClick={complete} disabled={busy}>
              <CheckCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Snooze">
          <span>
            <IconButton
              size="small"
              onClick={(e) => setSnoozeAnchor(e.currentTarget)}
              disabled={busy}
            >
              <SnoozeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={snoozeAnchor}
          open={Boolean(snoozeAnchor)}
          onClose={() => setSnoozeAnchor(null)}
        >
          <MenuItem onClick={() => snooze(1)}>1 day</MenuItem>
          <MenuItem onClick={() => snooze(3)}>3 days</MenuItem>
          <MenuItem onClick={() => snooze(7)}>7 days</MenuItem>
        </Menu>
        <Tooltip title="Edit">
          <span>
            <IconButton
              size="small"
              onClick={() => setEditOpen(true)}
              disabled={busy}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton size="small" onClick={remove} disabled={busy}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <ReminderEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={onChanged}
        initial={{
          _id: id,
          title: reminder.title,
          note: reminder.note,
          dueAt: reminder.dueAt,
          firm_name: reminder.firm_name,
        }}
      />
    </Box>
  );
};

export default ReminderRow;
